import {Button, Modal} from "react-bootstrap";
import {Formik} from "formik";
import * as Yup from "yup";

import {useUpdateIntervention} from "../../../hooks/useInterventions";
import {userRoleList} from "../../../utils/constants";
import {
  combineFrenchDateAndTime,
  formatFrenchDateInput,
  formatFrenchTimeInput,
  parseFrenchDate,
} from "../../../utils/date";
import {
  FrenchDateField,
  FrenchTimeField,
} from "../shared/FrenchDateTimeFields";
import {showToastModal} from "../shared/ToastModalSwal";

const ReportModal = ({
  openModal,
  toggleModal,
  intervention,
  initialValues,
  role,
}) => {
  const {onUpdateIntervention} = useUpdateIntervention();
  const previousAppointment = intervention?.startDate
    ? new Date(intervention.startDate)
    : null;

  const handleSubmit = async ({date, time}) => {
    const combinedDateTime = combineFrenchDateAndTime(date, time);
    const reportAction =
      role === userRoleList.CLIENT
        ? {reported_byClient: true}
        : {reported_byTech: true};

    try {
      await onUpdateIntervention({
        id: intervention?.id,
        body: {
          startDate: combinedDateTime.toISOString(),
          ...reportAction,
        },
      });
      toggleModal(false);
    } catch (error) {
      showToastModal({
        success: false,
        title: "Report impossible",
        message:
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Impossible de reporter l'intervention. Veuillez réessayer.",
        buttonTitle: "Fermer",
      });
    }
  };

  const validationSchema = Yup.object({
    date: Yup.string()
      .required("La date du RDV est requise")
      .test(
        "is-valid-date",
        "Date du RDV invalide (format attendu : jj/mm/aaaa)",
        (value) => Boolean(parseFrenchDate(value)),
      ),
    time: Yup.string()
      .matches(
        /^([01]\d|2[0-3]):([0-5]\d)$/,
        "L'heure doit être au format 24 h (HH:mm)",
      )
      .required("L'heure est requise")
      .test(
        "is-after-previous-appointment",
        "La nouvelle date et heure doivent être postérieures au rendez-vous actuel",
        function (value) {
          const selectedDateTime = combineFrenchDateAndTime(
            this.parent.date,
            value,
          );
          if (!selectedDateTime) return true;

          const minimumTimestamp = Math.max(
            previousAppointment?.getTime() || 0,
            Date.now(),
          );
          return selectedDateTime.getTime() > minimumTimestamp;
        },
      ),
  });

  return (
    <Modal
      className="modal fade"
      show={openModal}
      onHide={toggleModal}
      centered
      size="md"
    >
      <div className="modal-content">
        <div className="modal-header">
          <h4 className="modal-title text-[#1A64B9] font-semibold">
            Reporter l'intervention
          </h4>
          <Button
            variant=""
            type="button"
            className="close"
            onClick={() => toggleModal(false)}
          >
            <span>×</span>
          </Button>
        </div>
        <div className="modal-body !py-3 !px-0 h-fit max-h-[70vh] overflow-scroll overflow-style-none">
          <div className="w-full flex flex-col justify-center items-center gap-2">
            <div className="flex flex-col justify-center items-center">
              <div className="text-base font-normal !px-6 text-[#19295C] text-center">
                Veuillez indiquer la nouvelle date et heure du rendez-vous pour
                l'intervention « {intervention?.title} »
              </div>
            </div>

            <Formik
              enableReinitialize
              initialValues={{
                date: formatFrenchDateInput(
                  intervention?.startDate || initialValues?.date,
                ),
                time:
                  initialValues?.time ||
                  formatFrenchTimeInput(intervention?.startDate),
              }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({errors, touched, handleSubmit, isSubmitting}) => (
                <form onSubmit={handleSubmit} className="w-full">
                  <div className="w-full !px-6 !py-3">
                    <div className="form-group mb-4">
                      <label>Date du RDV</label>
                      <FrenchDateField
                        className="form-control placeholder:text-sm"
                        name="date"
                        aria-required="true"
                      />
                      {errors.date && touched.date && (
                        <div className="text-red-500 text-sm mt-1">
                          {errors.date}
                        </div>
                      )}
                    </div>
                    <div className="form-group mb-4">
                      <label>Heure du RDV</label>
                      <FrenchTimeField
                        className="form-control placeholder:text-sm"
                        name="time"
                        aria-required="true"
                      />
                      {errors.time && touched.time && (
                        <div className="text-red-500 text-sm mt-1">
                          {errors.time}
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-2 mt-4">
                      <button
                        type="button"
                        onClick={() => toggleModal(false)}
                        className="!rounded-md !py-3 bg-white !text-[#0D44B1] !border-[#0D44B1] !border font-medium text-sm uppercase"
                      >
                        Ignorer
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="!rounded-md !py-3 text-white bg-gradient-to-r from-[#0022AA] to-[#1A64B9] font-medium text-sm uppercase disabled:opacity-60"
                      >
                        {isSubmitting ? "Envoi..." : "Confirmer"}
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ReportModal;
